import { useEffect, useRef } from "react";
import { MISSION_ICONS, MISSION_VIDEO } from "@/mock";
import { useLanguage } from "@/i18n/LanguageContext";

const Mission = () => {
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    const tryPlay = () => {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => (e.isIntersecting ? tryPlay() : v.pause())),
      { threshold: 0.25 }
    );
    io.observe(v);
    v.addEventListener("canplay", tryPlay);
    tryPlay();
    return () => {
      io.disconnect();
      v.removeEventListener("canplay", tryPlay);
    };
  }, []);

  return (
    <section id="mission" data-testid="mission-section" className="bg-[#F4F4F2] py-8 md:py-10">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-8">
        <div className="flex flex-col items-center gap-6">
          <div className="reveal w-full max-w-3xl">
            <div className="text-[#2E4FE6] font-bold text-xs tracking-[0.22em] uppercase mb-5">{t.mission.eyebrow}</div>
            <h2 className="font-display text-[clamp(1.9rem,4.5vw,3.8rem)] uppercase text-[#111] break-words max-w-4xl mx-auto">{t.mission.title}</h2>
          </div>
          <div className="reveal relative aspect-video rounded-sm overflow-hidden bg-neutral-900 shadow-xl w-full max-w-3xl">
            <video
              ref={videoRef}
              data-testid="mission-video"
              src={MISSION_VIDEO}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="auto"
            />
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {t.mission.cards.map((c, i) => {
            const Icon = MISSION_ICONS[i];
            return (
              <div
                key={i}
                data-testid={`mission-card-${i}`}
                className="reveal bg-white rounded-lg p-7 border border-neutral-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="w-11 h-11 rounded-md bg-[#2E4FE6] flex items-center justify-center mb-5 mx-auto">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-archivo font-extrabold text-xl text-[#111] mb-2">{c.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{c.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Mission;
